const MATCH_ID_MARKER_REGEX = /\[match_id:(\d+)\]/;
const COACH_HIDDEN_TYPES = ['mention', 'update', 'last_match', 'response'];

export function parseMatchIdFromNotification(message, explicitMatchId = null) {
  if (explicitMatchId != null && explicitMatchId !== '') {
    const parsed = parseInt(explicitMatchId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const match = message?.match(MATCH_ID_MARKER_REGEX);
  return match ? parseInt(match[1], 10) : null;
}

export function stripMatchIdMarker(message) {
  if (!message) return '';
  return message.replace(/\n?\[match_id:\d+\]\s*/g, '').trim();
}

export function isAvailabilityNotification(notification) {
  if (!notification) return false;
  if (notification.matchId) return true;
  const text = stripMatchIdMarker(notification.message || notification.content || '').toLowerCase();
  return text.includes('availability') || text.includes('mark your child');
}

export function isCoachNotificationView() {
  const userType = sessionStorage.getItem('user_type');
  if (userType === 'coach' || userType === 'manager') {
    return true;
  }
  return sessionStorage.getItem('is_manager') === 'true'
    && sessionStorage.getItem('is_player_manager') !== 'true';
}

export function notificationRequestIds() {
  const childId = sessionStorage.getItem('player_id');
  const accountId = sessionStorage.getItem('user_id');
  const viewingAsParent = sessionStorage.getItem('is_parent') === 'true' && !isCoachNotificationView();
  return {
    childId,
    parentId: viewingAsParent ? accountId : null,
    requestPlayerId: viewingAsParent && accountId ? accountId : childId,
  };
}

export function isVisibleForParentProfile(row, { childId, parentId } = {}) {
  if (!parentId) {
    return true;
  }
  const owner = row?.player_id;
  if (owner == null || owner === '' || owner === 'null') {
    return true;
  }
  const ownerId = String(owner);
  if (childId && ownerId === String(childId)) {
    return true;
  }
  return ownerId === String(parentId);
}

export function isVisibleInCoachNotifications(notification) {
  if (notification?.preview_only || notification?.previewOnly) {
    return true;
  }
  return !COACH_HIDDEN_TYPES.includes(notification?.type);
}

export function parseProductNewsContent(announcement) {
  const raw = announcement.message || '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.body || parsed.content || parsed.title)) {
      return {
        title: parsed.title || 'Product News',
        content: parsed.body || parsed.content || '',
        image: parsed.image || announcement.image_url || null,
        buttonLabel: parsed.button_label || parsed.buttonLabel || null,
        buttonUrl: parsed.button_url || parsed.buttonUrl || null,
        newsType: parsed.news_type || parsed.newsType || 'announcement',
        newsId: parsed.news_id || announcement.news_id || null,
        audiences: Array.isArray(parsed.audiences) ? parsed.audiences : [],
        testOnly: !!parsed.test_only,
      };
    }
  } catch (e) {
    // Plain-text product news
  }
  const colonIndex = raw.indexOf(':');
  return {
    title: announcement.title || (colonIndex > 0 ? raw.substring(0, colonIndex).trim() : 'Product News'),
    content: colonIndex > 0 ? raw.substring(colonIndex + 1).trim() : raw,
    image: announcement.image_url || announcement.image || null,
    buttonLabel: null,
    buttonUrl: null,
    newsType: 'announcement',
    newsId: announcement.news_id || null,
    audiences: [],
    testOnly: false,
  };
}

export async function trackProductNewsClick(newsId, source = 'feed') {
  if (!newsId) return;
  try {
    const api = (await import('../services/apiService')).default;
    await api.post(`${process.env.REACT_APP_API_URL}/track_product_news_click.php`, {
      news_id: newsId,
      source,
    });
  } catch (err) {
    console.warn('trackProductNewsClick failed', err);
  }
}

export function resolveAchievementIdFromMessage(message = '') {
  const text = String(message || '').trim();
  if (!text) return '';

  if (text.includes('unlocked the')) {
    const startIdx = text.indexOf('unlocked the') + 12;
    const endIdx = text.indexOf('achievement', startIdx) - 1;
    if (endIdx > startIdx) {
      return text.substring(startIdx, endIdx)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
    }
  }

  return text
    .toLowerCase()
    .replace(/^unlocked\s+/i, '')
    .replace(/\sachievement$/i, '')
    .trim();
}

export function formatAchievementLabel(achievementId = '') {
  if (!achievementId) return 'Achievement';
  return achievementId
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function lookupAchievement(achievementsMap, achievementId) {
  if (!achievementId || !achievementsMap) return null;
  const raw = String(achievementId).trim();
  const kebab = raw.replace(/_/g, '-');
  const snake = raw.replace(/-/g, '_');
  return achievementsMap[raw]
    || achievementsMap[kebab]
    || achievementsMap[snake]
    || achievementsMap[raw.toLowerCase()]
    || achievementsMap[kebab.toLowerCase()]
    || achievementsMap[snake.toLowerCase()]
    || null;
}
